// src/services/serviceCatalog.js
import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareBaseQuery } from "./baseQueryWithReauth";

/**
 * Service Catalog API.
 *
 * Two base paths are involved:
 *   - The consume read endpoint lives UNDER the events router:
 *       GET /events/service-catalog
 *   - The admin CRUD endpoints live at the ROOT:
 *       GET   /service-catalog/admin
 *       POST  /service-catalog
 *       PUT   /service-catalog/:id
 *       PATCH /service-catalog/:id/archive
 *       PATCH /service-catalog/:id/restore
 *
 * To talk to both from a single createApi we base this api at the API ROOT
 * (empty relative base) and spell out the full relative path in each query.
 */
const serviceCatalogApi = createApi({
  reducerPath: "serviceCatalog",
  baseQuery: prepareBaseQuery(""),
  tagTypes: ["ServiceCatalog"],
  endpoints: (builder) => ({
    // Consume: active catalog with equipment-populated items (under /events/)
    getServiceCatalog: builder.query({
      query: () => ({ url: "events/service-catalog", method: "GET" }),
      providesTags: ["ServiceCatalog"],
      keepUnusedDataFor: 3600,
    }),

    // Standard service sets (base, wedding, corporate, secondLocation, venuePA)
    // plus the add-ons with prices: GET /events/service-presets (JWT).
    getServicePresets: builder.query({
      query: () => ({ url: "events/service-presets", method: "GET" }),
      keepUnusedDataFor: 3600,
    }),

    // Admin: full list including inactive/archived (root /service-catalog/admin)
    getServiceCatalogAdmin: builder.query({
      query: () => ({ url: "service-catalog/admin", method: "GET" }),
      providesTags: ["ServiceCatalog"],
    }),

    createService: builder.mutation({
      query: (body) => ({
        url: "service-catalog",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ServiceCatalog"],
    }),

    updateService: builder.mutation({
      query: ({ id, body }) => ({
        url: `service-catalog/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ServiceCatalog"],
    }),

    archiveService: builder.mutation({
      query: (id) => ({
        url: `service-catalog/${id}/archive`,
        method: "PATCH",
      }),
      invalidatesTags: ["ServiceCatalog"],
    }),

    restoreService: builder.mutation({
      query: (id) => ({
        url: `service-catalog/${id}/restore`,
        method: "PATCH",
      }),
      invalidatesTags: ["ServiceCatalog"],
    }),
  }),
});

export const {
  useGetServiceCatalogQuery,
  useGetServicePresetsQuery,
  useGetServiceCatalogAdminQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useArchiveServiceMutation,
  useRestoreServiceMutation,
} = serviceCatalogApi;

export default serviceCatalogApi;
export { serviceCatalogApi };
